import React from 'react';
import { FaPlus, FaMinus, FaTrash } from 'react-icons/fa';
import { CartItem as CartItemType } from '../../types/cart';
import { getProductImage } from '../../utils/productUtils';

interface CartItemProps {
  item: CartItemType;
  index: number;
  onUpdateQuantity: (id: number, identifier: any, newQuantity: number) => void;
  onRemoveItem: (id: number, identifier: any) => void;
}

const CartItem: React.FC<CartItemProps> = ({ item, index, onUpdateQuantity, onRemoveItem }) => {
  // Item identifier'ını belirle (eski veya yeni format)
  const getItemIdentifier = () => {
    if (item.dynamicOptions) {
      return { dynamicOptions: item.dynamicOptions };
    }
    return { size: item.options?.size, strength: item.options?.strength };
  };

  // Dinamik seçenekleri göstermek için yardımcı fonksiyon
  const renderDynamicOptions = () => {
    if (!item.dynamicOptions) {
      return null;
    }

    const optionColors = [
      'border-blue-300 bg-blue-50 text-blue-700',
      'border-purple-300 bg-purple-50 text-purple-700',
      'border-green-300 bg-green-50 text-green-700',
      'border-orange-300 bg-orange-50 text-orange-700',
    ];

    return Object.entries(item.dynamicOptions).map(([key, value], idx) => (
      <span
        key={key}
        className={`px-2 py-0.5 rounded-full text-xs font-medium ${optionColors[idx % optionColors.length]}`}
      >
        {value}
      </span>
    ));
  };

  return (
    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
      <div className="w-16 h-16 flex items-center justify-center bg-white rounded-lg border border-gray-100 flex-shrink-0">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-cover rounded-lg"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {getProductImage(item.main_category_id || '1', item.category_id || '4', 'w-10 h-10')}
          </div>
        )}
      </div>

      <div className="flex-grow min-w-0">
        <h3 className="font-semibold text-gray-800 truncate">{item.name}</h3>
        <div className="flex flex-wrap gap-2 mt-1">
          {/* Eski format seçenekleri */}
          {item.has_options && item.options?.size && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium border border-green-300 bg-green-50 text-green-700">
              {item.options.size === 'normal' ? 'Normal' : 'Double'}
            </span>
          )}
          {item.has_options && item.options?.strength && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium border border-yellow-300 bg-yellow-50 text-yellow-700">
              {item.options.strength === 'light'
                ? 'Açık'
                : item.options.strength === 'strong'
                  ? 'Demli'
                  : 'Normal'}
            </span>
          )}

          {/* Yeni format dinamik seçenekleri */}
          {renderDynamicOptions()}
        </div>
        <div className="text-green-600 font-medium text-sm mt-1">
          {item.price * item.quantity} Fiş
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => onUpdateQuantity(item.id, getItemIdentifier(), item.quantity - 1)}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          disabled={item.quantity <= 1}
          aria-label="Azalt"
        >
          <FaMinus size={14} />
        </button>
        <span className="w-7 text-center font-medium">{item.quantity}</span>
        <button
          onClick={() => onUpdateQuantity(item.id, getItemIdentifier(), item.quantity + 1)}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Arttır"
        >
          <FaPlus size={14} />
        </button>
        <button
          onClick={() => onRemoveItem(item.id, getItemIdentifier())}
          className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          aria-label="Sil"
        >
          <FaTrash size={14} />
        </button>
      </div>
    </div>
  );
};

export default CartItem;
